/**
 * PR-ONB-WIZARD-AREA-UX-1 — pure flow: propagate a freshly-verified email to
 * the backend when the applicant returns to the area, WITHOUT requiring a
 * manual "Ho verificato l'email" click or a full logout/login.
 *
 * The BE persists `email_verified_at` only when an authenticated /area request
 * arrives with an ID token whose `email_verified` claim is true
 * (onboardingApplicantAuth middleware). A plain reload reuses the cached token
 * (stale claim) — so we must reload the Firebase user, force a FRESH token, then
 * re-read /area/me (which the guard performs with that fresh token).
 *
 * Mirrors `areaSubmitFlow` / `areaIdentityStartFlow`. No React/DOM → testable.
 */

export interface AreaEmailRefreshDeps {
  /** Firebase `user.reload()` — refreshes the user record (emailVerified). */
  reloadUser: () => Promise<void>;
  /** `getIdToken(force)`. MUST be called with force=true to carry the new claim. */
  getToken: (force: boolean) => Promise<string | null>;
  /** Re-fetch /area/me — the guard re-fires GET /me with the fresh token. */
  refresh: () => void;
}

/**
 * reloadUser → getIdToken(true) → refresh(/me). Returns true when a fresh token
 * was obtained (i.e. the refreshed claim could be carried to the BE). Never
 * throws on a null token; the caller decides how to surface a transient miss.
 */
export async function performAreaEmailRefresh(
  deps: AreaEmailRefreshDeps,
): Promise<boolean> {
  await deps.reloadUser();
  const token = await deps.getToken(true);
  deps.refresh();
  return token !== null;
}
